import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pill, Sparkles, AlertTriangle, PhoneCall } from "lucide-react";

/**
 * Modal that pops up when a visitor clicks a medication card on
 * /browse-medications.  Uses the same "illuminated" backdrop pattern as the
 * FactOrMyth modal on the landing page — aurora blobs + floating icons + a
 * blurred gradient — so the public side keeps a consistent science-y feel.
 *
 * NO pricing is shown here.  Visitors are directed to the qualifying quiz
 * for pricing (single source of truth).
 *
 * Body content is sourced from `MED_INFO[name]` keyed by the medication's
 * display name; falls back to a generic GLP-1 explainer if the medication
 * isn't in the dictionary so newly-added meds still get a useful pop-up.
 */
// Serious-risks copy — DRAFT based on FDA labelling for the branded
// GLP-1 products (Ozempic / Wegovy / Mounjaro / Zepbound) that share the
// same active ingredient. Compounded formulations have not been evaluated
// by the FDA, so this is educational, not exhaustive. Every risk here
// must be reviewed and signed off by the medical director before public
// LegitScript submission.
const SHARED_RISKS = [
  "**Thyroid C-cell tumours** — do not use if you or a family member has a history of medullary thyroid carcinoma or Multiple Endocrine Neoplasia syndrome type 2 (MEN 2). Tell your clinician about any new neck lump, hoarseness, trouble swallowing, or shortness of breath.",
  "**Pancreatitis** — severe, persistent abdominal pain (sometimes radiating to the back), with or without vomiting, can indicate acute pancreatitis. Stop the medication and seek medical care immediately.",
  "**Gallbladder problems** — gallstones and acute cholecystitis have been reported. Watch for upper-right belly pain, fever, yellowing of the skin, or clay-coloured stools.",
  "**Dehydration and acute kidney injury** — nausea, vomiting, or diarrhoea can cause dehydration and, in rare cases, worsening kidney function. Sip fluids throughout the day and call your clinician if symptoms persist.",
  "**Low blood sugar (hypoglycaemia)** — risk is meaningfully higher if you also take insulin or a sulfonylurea. Your clinician may need to adjust those doses.",
  "**Serious allergic reactions** — call 911 for facial, tongue, or throat swelling, difficulty breathing, hives, or a severe rash after an injection.",
  "**Pregnancy, breastfeeding, and family planning** — GLP-1 medications are contraindicated during pregnancy and are not recommended while breastfeeding. Compounded formulations have not been evaluated by the FDA for use in pregnancy. Stop the medication and contact your clinician if you become pregnant or plan to become pregnant.",
  "**Slowed stomach emptying + anesthesia** — GLP-1s can delay gastric emptying. Tell any surgeon, anesthesiologist, or endoscopist before a procedure that you are on this medication.",
];
const SEMA_EXTRA = [
  "**Diabetic retinopathy complications** — patients with type 2 diabetes and existing eye disease should be monitored during semaglutide therapy.",
];

const MED_INFO = {
  "Semaglutide": {
    tagline: "Once-weekly GLP-1 receptor agonist",
    summary: "Semaglutide is a GLP-1 receptor agonist that mimics a natural hormone your gut releases after eating. It supports weight management by helping you feel full sooner, reducing food cravings, and slowing how quickly your stomach empties.",
    facts: [
      "Once-weekly injection — typically given subcutaneously into the abdomen, thigh, or upper arm.",
      "Used clinically for both Type 2 diabetes and chronic weight management.",
      "Most people start at 0.25 mg and titrate up under physician guidance to find their effective dose.",
    ],
    risks: [...SHARED_RISKS, ...SEMA_EXTRA],
    notes: "Not appropriate for everyone — a licensed clinician will review your full medical history to confirm it's safe for you. Common early side effects (mild nausea, stomach upset) usually improve as your body adjusts.",
  },
  "Tirzepatide": {
    tagline: "Dual GIP + GLP-1 receptor agonist",
    summary: "Tirzepatide is a newer dual-action medication that activates both the GLP-1 and GIP receptors in your gut. The combined mechanism appears to support stronger appetite regulation and metabolic effects than GLP-1 alone for many patients.",
    facts: [
      "Once-weekly subcutaneous injection.",
      "FDA-approved (as branded Mounjaro / Zepbound) for Type 2 diabetes and chronic weight management. Compounded tirzepatide is not FDA-approved.",
      "Dosing typically starts at 2.5 mg with gradual titration over several months.",
    ],
    risks: SHARED_RISKS,
    notes: "Like semaglutide, tirzepatide is prescription-only and requires clinician oversight. Common early side effects (nausea, mild stomach upset) usually fade with continued use and slow titration.",
  },
};

function lookupMedInfo(med) {
  if (!med) return null;
  // Normalise "Semaglutide with B12" → "Semaglutide" for the dictionary lookup.
  const base = (med.name || "").split(/\s+with\s+/i)[0].trim();
  return MED_INFO[base] || {
    tagline: med.type || "GLP-1 receptor agonist",
    summary: med.description || "Physician-prescribed GLP-1 therapy. A licensed clinician will review your full medical history to confirm it's safe and appropriate for you.",
    facts: [
      "Once-weekly subcutaneous injection.",
      "Prescription-only — every order is reviewed by a US-licensed clinician.",
      "Compounded by 503A/503B partner pharmacies under physician oversight.",
    ],
    risks: SHARED_RISKS,
    notes: "Side effects most commonly include mild nausea or stomach upset, which usually improve as your body adjusts. Discuss any medication, allergy, or medical-history concerns with your clinician.",
  };
}

export default function MedicationInfoModal({ med, onClose }) {
  // Close on Escape.
  useEffect(() => {
    if (!med) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [med, onClose]);

  const info = lookupMedInfo(med);
  const baseName = (med?.name || "").split(/\s+with\s+/i)[0].trim() || "Medication";

  return (
    <AnimatePresence>
      {med && info && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-hidden"
          data-testid="med-info-modal"
        >
          {/* Illuminated layered backdrop — same pattern as FactOrMyth modal */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-slate-900/85 to-indigo-900/80 backdrop-blur-sm" aria-hidden />
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
            <motion.div
              className="absolute -left-40 -top-40 w-[28rem] h-[28rem] rounded-full opacity-40"
              style={{ background: "radial-gradient(circle, rgba(147,51,234,0.55), transparent 60%)" }}
              animate={{ x: [0, 40, -20, 0], y: [0, 20, -30, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute -right-32 top-1/3 w-[24rem] h-[24rem] rounded-full opacity-40"
              style={{ background: "radial-gradient(circle, rgba(56,189,248,0.45), transparent 60%)" }}
              animate={{ x: [0, -30, 20, 0], y: [0, -20, 15, 0] }}
              transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute left-1/4 -bottom-40 w-[26rem] h-[26rem] rounded-full opacity-30"
              style={{ background: "radial-gradient(circle, rgba(244,114,182,0.45), transparent 60%)" }}
              animate={{ x: [0, 20, -10, 0], y: [0, -15, 10, 0] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            {Array.from({ length: 12 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-white/40"
                style={{
                  left: `${(i * 73) % 100}%`,
                  top: `${(i * 37) % 100}%`,
                  width: 4 + (i % 3) * 2,
                  height: 4 + (i % 3) * 2,
                }}
                animate={{ opacity: [0.15, 0.6, 0.15], y: [0, -10, 0] }}
                transition={{ duration: 4 + (i % 5), repeat: Infinity, delay: (i % 6) * 0.4, ease: "easeInOut" }}
              />
            ))}
          </div>

          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            data-testid="med-info-modal-card"
          >
            <div className="relative px-6 sm:px-8 pt-6 pb-2">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                aria-label="Close"
                data-testid="med-info-close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-md shadow-purple-200">
                  <Pill className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-2xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="med-info-title">
                    {baseName}
                  </h3>
                  <p className="text-xs uppercase tracking-wider text-purple-600 font-semibold mt-0.5">
                    {info.tagline}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 sm:px-8 pb-6">
              <p className="text-sm text-slate-700 leading-relaxed mt-4">
                {info.summary}
              </p>

              <div className="mt-5 rounded-xl bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-100 px-4 py-3.5">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-purple-700 mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Quick facts
                </p>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  {info.facts.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">•</span>
                      <span className="flex-1">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {info.notes && (
                <p className="mt-4 text-xs text-slate-500 leading-relaxed italic">
                  {info.notes}
                </p>
              )}

              {info.risks && info.risks.length > 0 && (
                <div
                  className="mt-5 rounded-xl border border-amber-300 bg-amber-50/70 px-4 py-3.5"
                  data-testid="med-info-serious-risks"
                >
                  <p className="text-[11px] uppercase tracking-wider font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Serious risks — read before you start
                  </p>
                  <ul className="space-y-2 text-[13px] text-amber-950 leading-relaxed">
                    {info.risks.map((r, i) => {
                      const parts = r.split(/\*\*(.+?)\*\*/g);
                      return (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-amber-700 mt-0.5">•</span>
                          <span className="flex-1">
                            {parts.map((p, j) => (j % 2 === 1 ? <strong key={j}>{p}</strong> : <span key={j}>{p}</span>))}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-amber-200 flex items-start gap-2 text-[12px] text-amber-900" data-testid="med-info-when-to-seek-help">
                    <PhoneCall className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <p>
                      <strong>When to seek help immediately:</strong> severe or persistent belly pain,
                      signs of an allergic reaction (facial or throat swelling, trouble breathing),
                      dehydration you can&apos;t keep up with, sudden vision changes, or any symptom
                      that feels like an emergency — call 911 or your local emergency service.
                    </p>
                  </div>
                  <p className="mt-3 text-[11px] text-amber-800 italic">
                    Compounded GLP-1 medications are <strong>not FDA-approved</strong> and have not been
                    reviewed by the FDA for safety, effectiveness, or quality before marketing. This
                    risk list is educational and not exhaustive — your clinician will review your
                    complete history before any prescription is written.
                  </p>
                </div>
              )}

              <p className="mt-5 text-[11px] text-slate-400 leading-relaxed">
                This is general educational information, not medical advice.
                Take our 30-second eligibility quiz to see if this medication is
                appropriate for you — a US-licensed clinician will review your
                full history before any prescription is written.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
